import { forwardRef } from 'react'
import { ScoopSpinner } from '../ScoopSpinner/ScoopSpinner'
import styles from './ScoopButton.module.scss'

/**
 * ScoopButton - A flexible, native HTML button component with multiple variants and sizes
 *
 * @component
 * @param {Object} props - Component props
 * @param {'default' | 'outline' | 'outlineGray' | 'ghost' | 'text' | 'danger' | 'grey' | 'icon'} [props.variant='default'] - Button style variant
 *   - default: Pink/red background with white text (primary brand color)
 *   - outline: Transparent with colored border
 *   - outlineGray: Transparent with grey border and text
 *   - ghost: Transparent with no border
 *   - text: Only text without background (for secondary actions like Cancel)
 *   - danger: Red background for destructive actions
 *   - grey: Grey background with dark text
 *   - icon: Icon-only button with minimal styling (square aspect ratio)
 * @param {'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'fullWidth'} [props.size='sm'] - Button size
 *   - xxs: Extra extra small (24px height, 11px font)
 *   - xs: Extra small (28px height, 12px font)
 *   - sm: Small (43px height, 14px font) - DEFAULT
 *   - md: Medium (50px height, 16px font)
 *   - lg: Large (56px height, 18px font)
 *   - fullWidth: 100% width of container
 * @param {React.ReactNode} [props.leftSection] - Content to display on the left side (typically an icon)
 * @param {React.ReactNode} [props.rightSection] - Content to display on the right side (typically an icon)
 * @param {boolean} [props.disabled=false] - Whether the button is disabled
 * @param {boolean} [props.loading=false] - Whether to show loading spinner (replaces leftSection when true)
 * @param {string} [props.className] - Additional CSS classes to apply to the button
 * @param {Object} [props.style] - Inline styles (highest priority - overrides all other styles)
 * @param {React.ReactNode} props.children - Button content/label
 * @param {'button' | 'submit' | 'reset'} [props.type='button'] - HTML button type attribute
 * @param {function} [props.onClick] - Click handler function
 * @param {React.Ref} [ref] - Forwarded ref to the button element
 * @param {...any} props - All other props are passed to the native HTML button element
 *
 */
export const ScoopButton = forwardRef(
    (
        {
            variant = 'default',
            size = 'sm',
            leftSection,
            rightSection,
            disabled = false,
            loading = false,
            className = '',
            children,
            type = 'button',
            style,
            ...props
        },
        ref
    ) => {
        const buttonClassName = [
            styles.button,
            styles[variant] || styles.default,
            styles[size] || styles.md,
            disabled && styles.disabled,
            loading && styles.loading,
            className,
        ]
            .filter(Boolean)
            .join(' ')

        return (
            <button
                ref={ref}
                type={type}
                className={buttonClassName}
                style={style}
                disabled={disabled || loading}
                {...props}
            >
                {loading ? (
                    <ScoopSpinner
                        size={size === 'xxs' ? 14 : size === 'xs' ? 16 : size === 'sm' ? 18 : 20}
                        style={{
                            filter:
                                variant === 'default' || variant === 'danger'
                                    ? 'brightness(0) invert(1)'
                                    : 'none',
                        }}
                    />
                ) : (
                    leftSection && <span className={styles.leftSection}>{leftSection}</span>
                )}
                {children && <span className={styles.content}>{children}</span>}
                {!loading && rightSection && (
                    <span className={styles.rightSection}>{rightSection}</span>
                )}
            </button>
        )
    }
)

ScoopButton.displayName = 'ScoopButton'

export default ScoopButton
